import { Box, Flex, Loader, Center } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { EditorHelpers } from 'src/common/components/markdown-editor/plugins/EditorReadyPlugin';
import { GroupedSuggestions } from 'src/common/components/markdown-editor/plugins/SuggestionTrackerPlugin';
import { useAppContainerContext, useContentMeta } from 'src/UI/app-container';
import {
  ChatMode,
  ChatState,
  FinalChunkData,
} from 'src/UI/app-container/chat/hooks/useChatMessages';
import { ContentPaneOverlay } from 'src/UI/app-container/ContentPaneOverlay';
import { SiestaMode } from 'src/UI/app-container/types';
import { useAppContainer } from 'src/UI/app-container/useAppContainer';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { useCreateDocumentVersion } from '../hooks/useCreateDocumentVersion';
import { useDeleteDocumentVersions } from '../hooks/useDeleteDocumentVersions';
import { useRecordConversationDiscard } from '../hooks/useRecordConversationDiscard';
import { useDeleteDocument } from '../hooks/useDeleteDocument';
import { useDocumentVersions } from '../hooks/useDocumentVersions';
import { useRevertToVersion } from '../hooks/useRevertToVersion';
import { useSaveDocument } from '../hooks/useSaveDocument';
import { useSetPrimaryVersion } from '../hooks/useSetPrimaryVersion';
import { DocumentPanel } from './components';
import { EditingOverlay } from './components/EditingOverlay';
import { EditPreviewSheet } from './components/EditPreviewSheet';
import SiestaActionBar from './components/SiestaActionBar';
import { prefetchSuggestedActions } from 'src/UI/app-container/sidebar/prefetchSuggestedActions';
import { formatVersionDate } from './utils/formatVersionDate';

const EditState = {
  IDLE: 'IDLE',
  EDITING: 'EDITING',
  PREVIEW: 'PREVIEW',
  DIFF_REVIEW: 'DIFF_REVIEW',
} as const;

type EditState = (typeof EditState)[keyof typeof EditState];

type EditStateData = {
  state: EditState;
  editedContent?: string;
  originalContent?: string;
  assistantMessage?: string;
  versionId?: string | null;
  versionNumber?: number | null;
};

export type DocumentEditConfig = {
  pageType:
    | 'suitability-report-edit'
    | 'data-export-edit'
    | 'meeting-agenda-edit'
    | 'meeting-note-edit';
  title: string;
  documentTypePath: 'suitability-reports' | 'data-exports' | 'meeting-agendas' | 'meeting-notes';
  documentLabel: string;
};

type Props = {
  config: DocumentEditConfig;
};

export default function DocumentEditPage({ config }: Props) {
  const { clientId, id } = useParams<{ clientId: string; id: string }>();
  const navigate = useNavigate();
  const {
    deleteDocument,
    error: deleteDocumentError,
    reset: resetDeleteDocument,
    isLoading: isRemovingDocument,
  } = useDeleteDocument(config.documentTypePath);
  const [suggestions, setSuggestions] = useState<GroupedSuggestions>({
    default: {},
    note: {},
    inline: {},
    placeholder: {},
    deletion: {},
  });
  const [showMarkers, setShowMarkers] = useState(true);
  const { versions, isLoading: isLoadingVersions, refetch } = useDocumentVersions(id);
  const { createDocumentVersion } = useCreateDocumentVersion(id);
  const { deleteDocumentVersions } = useDeleteDocumentVersions(id);
  const { recordConversationDiscard } = useRecordConversationDiscard();
  const { setPrimaryVersion } = useSetPrimaryVersion(id);
  const { saveDocument } = useSaveDocument(id);
  const { revertToVersion } = useRevertToVersion(id);
  const { createLink } = useFileTemporaryLinkMutation();
  const editorHelpersRef = useRef<EditorHelpers | null>(null);
  const previousSiestaModeRef = useRef<SiestaMode | null>(null);
  const actionBarRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef(suggestions);

  const handleSuggestionsChange = useCallback((newSuggestions: GroupedSuggestions) => {
    suggestionsRef.current = newSuggestions;
    setSuggestions(newSuggestions);
  }, []);
  const { siestaMode, setSiestaMode, setSiestaBusyGlowTop } = useAppContainer();
  const { conversationId } = useAppContainerContext();
  const queryClient = useQueryClient();

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [editorInstanceKey, setEditorInstanceKey] = useState<string | null>(null);
  const preInlineEditContentRef = useRef<string | null>(null);
  const lastSavedContentRef = useRef<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editState, setEditState] = useState<EditStateData>({
    state: EditState.IDLE,
  });

  const inlineSuggestions = useMemo(
    () => Object.values(suggestions.inline || {}),
    [suggestions.inline],
  );

  const deletionSuggestions = useMemo(
    () => Object.values(suggestions.deletion || {}),
    [suggestions.deletion],
  );

  const diffSuggestions = useMemo(
    () => [...inlineSuggestions, ...deletionSuggestions],
    [inlineSuggestions, deletionSuggestions],
  );

  const primaryVersion = useMemo(() => versions.find(v => v.isPrimary), [versions]);

  const defaultVersion = useMemo(() => {
    if (primaryVersion) return primaryVersion;
    if (versions.length === 0) return undefined;
    return versions.reduce((latest, v) => (v.versionNumber > latest.versionNumber ? v : latest));
  }, [versions, primaryVersion]);

  const selectedVersion = useMemo(() => {
    if (selectedVersionId) {
      const found = versions.find(v => v.id === selectedVersionId);
      if (found) return found;
    }
    return defaultVersion;
  }, [versions, selectedVersionId, defaultVersion]);

  const isViewingPrimary = selectedVersion?.id === primaryVersion?.id;

  useEffect(() => {
    if (diffSuggestions.length > 0 && actionBarRef.current) {
      const rect = actionBarRef.current.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const percent = (centerY / window.innerHeight) * 100;
      setSiestaBusyGlowTop(percent);
    }
  }, [diffSuggestions.length, setSiestaBusyGlowTop]);

  const getDocumentContent = useCallback(
    () => editorHelpersRef.current?.getContent() ?? selectedVersion?.markdownContent ?? '',
    [selectedVersion?.markdownContent],
  );

  const contentMetadata = useMemo(
    () =>
      selectedVersion?.markdownContent
        ? { documentContent: selectedVersion.markdownContent }
        : undefined,
    [selectedVersion?.markdownContent],
  );

  const handleChatStateChange = useCallback((state: ChatState, data?: FinalChunkData) => {
    if (state === ChatState.EDITING) {
      setEditState(prev => {
        if (prev.state === EditState.DIFF_REVIEW) return prev;
        const currentContent = editorHelpersRef.current?.getContent();
        return {
          ...prev,
          state: EditState.EDITING,
          originalContent: currentContent,
        };
      });
    } else if (state === ChatState.FINAL) {
      if (data && data.editedContent !== undefined) {
        const contentChanged = data.editedContent !== editorHelpersRef.current?.getContent();
        if (contentChanged) {
          if (data.editedContentWithMarkers) {
            editorHelpersRef.current?.setContent(data.editedContentWithMarkers, () => {
              requestAnimationFrame(() => {
                const firstInline = Object.values(suggestionsRef.current.inline || {})[0];
                const firstDeletion = Object.values(suggestionsRef.current.deletion || {})[0];
                const firstSuggestion = firstInline ?? firstDeletion;
                firstSuggestion?.scrollIntoView();
              });
            });
            editorHelpersRef.current?.setEditable(false);
            setEditState(prev => ({
              state: EditState.DIFF_REVIEW,
              editedContent: data.editedContent,
              originalContent: prev.originalContent,
              assistantMessage: data.assistantMessage,
              versionId: data.versionId,
              versionNumber: data.versionNumber,
            }));
          } else {
            setEditState(prev => ({
              state: EditState.PREVIEW,
              editedContent: data.editedContent,
              originalContent: prev.originalContent,
              assistantMessage: data.assistantMessage,
              versionId: data.versionId,
              versionNumber: data.versionNumber,
            }));
            editorHelpersRef.current?.setContent(data.editedContent);
          }
        } else {
          setEditState({ state: EditState.IDLE });
        }
      } else {
        setEditState({ state: EditState.IDLE });
      }
    } else if (state === ChatState.ANSWERING || state === ChatState.THINKING) {
      setEditState(prev => {
        if (
          prev.state === EditState.EDITING ||
          prev.state === EditState.PREVIEW ||
          prev.state === EditState.DIFF_REVIEW
        ) {
          return prev;
        }
        return { state: EditState.IDLE };
      });
    }
  }, []);

  useContentMeta({
    pageType: config.pageType,
    entityId: id,
    metadata: contentMetadata,
    chatMode: ChatMode.DOCUMENT,
    onChatStateChange: handleChatStateChange,
    getDocumentContent,
  });

  const handleApplyPreview = useCallback(async () => {
    if (editState.versionId) {
      const wasPrimary = selectedVersion?.isPrimary;
      if (wasPrimary) {
        await setPrimaryVersion(editState.versionId);
      }
      await refetch();
      setEditorInstanceKey(current => current ?? selectedVersion?.id ?? null);
      setSelectedVersionId(editState.versionId);
    } else {
      await refetch();
    }

    setEditState({ state: EditState.IDLE });
  }, [
    editState.versionId,
    selectedVersion?.isPrimary,
    selectedVersion?.id,
    setPrimaryVersion,
    refetch,
  ]);

  const handleDiscardPreview = useCallback(async () => {
    try {
      if (editState.versionId) {
        await deleteDocumentVersions([editState.versionId]);
        if (conversationId) await recordConversationDiscard(conversationId);
        await refetch();
      }
    } catch (error) {
      notifyAndLogError('Failed to discard preview', 'Please try again.', error, {
        documentId: id,
        versionId: editState.versionId,
      });
    }
    if (editState.originalContent) {
      editorHelpersRef.current?.setContent(editState.originalContent);
    }
    setEditState({ state: EditState.IDLE });
  }, [
    editState.originalContent,
    editState.versionId,
    deleteDocumentVersions,
    recordConversationDiscard,
    conversationId,
    refetch,
    id,
  ]);

  const handleEditorReady = useCallback((helpers: EditorHelpers) => {
    editorHelpersRef.current = helpers;
    lastSavedContentRef.current = helpers.getContent();
    setHasChanges(false);
  }, []);

  const handleDirty = useCallback(() => {
    setHasChanges(true);
  }, []);

  const handleSelectVersion = useCallback(
    async (versionId: string) => {
      try {
        await revertToVersion(versionId);
        setSelectedVersionId(versionId);
        setEditorInstanceKey(versionId);
      } catch (error) {
        notifyAndLogError('Failed to select version', 'Please try again.', error, {
          documentId: id,
          versionId,
        });
      }
    },
    [revertToVersion, id],
  );

  const handleMakePrimary = useCallback(
    (versionId: string) => {
      setPrimaryVersion(versionId);
      setSelectedVersionId(versionId);
      setEditorInstanceKey(versionId);
    },
    [setPrimaryVersion],
  );

  const handleSaveVersion = useCallback(async () => {
    try {
      const content = editorHelpersRef.current?.getContent();
      if (!content) return;
      await createDocumentVersion({ markdownContent: content });
      await refetch();
    } catch (error) {
      notifyAndLogError('Failed to save version', 'Please try again.', error, {
        documentId: id,
      });
    }
  }, [createDocumentVersion, refetch, id]);

  const handleRemoveVersion = useCallback(
    async (versionId: string) => {
      try {
        await deleteDocumentVersions([versionId]);
        await refetch();
      } catch (error) {
        notifyAndLogError('Failed to remove version', 'Please try again.', error, {
          documentId: id,
          versionId,
        });
      }
    },
    [deleteDocumentVersions, refetch, id],
  );

  const handleOverwriteVersion = useCallback(
    async (versionId: string) => {
      try {
        const content = editorHelpersRef.current?.getContent();
        if (!content) return;
        await deleteDocumentVersions([versionId]);
        await createDocumentVersion({ markdownContent: content });
        await refetch();
      } catch (error) {
        notifyAndLogError('Failed to overwrite version', 'Please try again.', error, {
          documentId: id,
          versionId,
        });
      }
    },
    [deleteDocumentVersions, createDocumentVersion, refetch, id],
  );

  const handleSave = useCallback(async () => {
    if (!selectedVersion) return;

    const content = editorHelpersRef.current?.getContent();
    if (!content) return;

    if (content === lastSavedContentRef.current) {
      setHasChanges(false);
      return;
    }

    setIsSaving(true);
    try {
      const wasPrimary = selectedVersion.isPrimary;
      const result = await createDocumentVersion({ markdownContent: content });
      const newVersionId = result.data.id;

      if (wasPrimary) {
        await setPrimaryVersion(newVersionId);
      }

      lastSavedContentRef.current = content;
      setHasChanges(false);
      await refetch();
      setSelectedVersionId(newVersionId);
      setEditorInstanceKey(newVersionId);
    } catch (error) {
      notifyAndLogError('Failed to save version', 'Please try again.', error, {
        documentId: id,
      });
    } finally {
      setIsSaving(false);
    }
  }, [selectedVersion, createDocumentVersion, setPrimaryVersion, refetch, id]);

  const backHref = `/client/${clientId}/document/${config.documentTypePath}`;

  const handleFinalise = useCallback(async () => {
    if (!primaryVersion) return;
    try {
      await saveDocument({ versionId: primaryVersion.id, setAsPrimary: true });
      const content = editorHelpersRef.current?.getContent() ?? primaryVersion.markdownContent;
      if (content && clientId && id) {
        prefetchSuggestedActions(queryClient, id, clientId, content);
      }
      navigate(backHref);
    } catch (error) {
      notifyAndLogError('Failed to finalise document', 'Please try again.', error, {
        documentId: id,
        versionId: primaryVersion.id,
      });
    }
  }, [primaryVersion, saveDocument, navigate, clientId, id, queryClient, backHref]);

  const handleDownloadPdf = useCallback(
    async (fileId: number) => {
      try {
        const link = await createLink(fileId);
        window.open(link.url, '_blank');
      } catch (error) {
        notifyAndLogError(
          'Failed to download PDF',
          'An error occurred while trying to download the PDF. Please try again later.',
          error,
          { documentId: id, fileId },
        );
      }
    },
    [createLink, id],
  );

  const handleRemoveDocument = useCallback(async () => {
    if (!clientId || !id) return;
    try {
      await deleteDocument(clientId, id);
      navigate(backHref);
    } catch (e) {
      notifyAndLogError(
        `Failed to delete ${config.documentLabel}`,
        `An error occurred while trying to delete the ${config.documentLabel}. Please try again later.`,
        e,
        { clientId, documentId: id },
      );
    }
  }, [clientId, id, deleteDocument, navigate, backHref, config.documentLabel]);

  const handleApplyDiffReview = useCallback(async () => {
    editorHelpersRef.current?.applyInlineSuggestions();
    editorHelpersRef.current?.setEditable(true);

    if (editState.versionId) {
      const wasPrimary = selectedVersion?.isPrimary;
      if (wasPrimary) {
        await setPrimaryVersion(editState.versionId);
      }
      await refetch();
      setEditorInstanceKey(current => current ?? selectedVersion?.id ?? null);
      setSelectedVersionId(editState.versionId);
    } else {
      await refetch();
    }

    setEditState({ state: EditState.IDLE });
  }, [
    editState.versionId,
    selectedVersion?.isPrimary,
    selectedVersion?.id,
    setPrimaryVersion,
    refetch,
  ]);

  const handleDiscardDiffReview = useCallback(async () => {
    try {
      if (editState.versionId) {
        await deleteDocumentVersions([editState.versionId]);
        if (conversationId) await recordConversationDiscard(conversationId);
        await refetch();
      }
    } catch (error) {
      notifyAndLogError('Failed to discard changes', 'Please try again.', error, {
        documentId: id,
        versionId: editState.versionId,
      });
    }
    if (editState.originalContent) {
      editorHelpersRef.current?.setContent(editState.originalContent);
    } else {
      editorHelpersRef.current?.discardSuggestions();
    }
    editorHelpersRef.current?.setEditable(true);
    setEditState({ state: EditState.IDLE });
  }, [
    editState.originalContent,
    editState.versionId,
    deleteDocumentVersions,
    recordConversationDiscard,
    conversationId,
    refetch,
    id,
  ]);

  const handleInlineEditStart = useCallback(
    (top: number) => {
      const content = editorHelpersRef.current?.getContent();
      if (content) {
        preInlineEditContentRef.current = content;
      }
      setSiestaBusyGlowTop(top);
      previousSiestaModeRef.current = siestaMode === 'busy' ? 'action' : siestaMode;
      setSiestaMode('busy');
    },
    [siestaMode, setSiestaMode, setSiestaBusyGlowTop],
  );

  const handleApplyInlineChanges = useCallback(() => {
    editorHelpersRef.current?.applyInlineSuggestions();
    preInlineEditContentRef.current = null;
    setSiestaBusyGlowTop(null);
    setSiestaMode(previousSiestaModeRef.current ?? 'action');
  }, [setSiestaMode, setSiestaBusyGlowTop]);

  const handleDiscardInlineChanges = useCallback(() => {
    if (preInlineEditContentRef.current) {
      editorHelpersRef.current?.setContent(preInlineEditContentRef.current);
    } else {
      editorHelpersRef.current?.discardSuggestions();
    }
    preInlineEditContentRef.current = null;
    setSiestaBusyGlowTop(null);
    setSiestaMode(previousSiestaModeRef.current ?? 'action');
  }, [setSiestaMode, setSiestaBusyGlowTop]);

  const handleInlineEditComplete = useCallback(() => {
    requestAnimationFrame(() => {
      const currentInlineSuggestions = Object.values(suggestionsRef.current.inline || {});
      const currentDeletionSuggestions = Object.values(suggestionsRef.current.deletion || {});
      if (
        currentInlineSuggestions.length === 0 &&
        currentDeletionSuggestions.length === 0 &&
        preInlineEditContentRef.current !== null
      ) {
        preInlineEditContentRef.current = null;
        setSiestaBusyGlowTop(null);
        setSiestaMode(previousSiestaModeRef.current ?? 'action');
      }
    });
  }, [setSiestaMode, setSiestaBusyGlowTop]);

  if (isLoadingVersions) {
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  }

  return (
    <Flex direction="column" h="100%" pos="relative">
      <ContentPaneOverlay>
        <EditingOverlay mounted={editState.state === EditState.EDITING} />
        <EditPreviewSheet
          opened={editState.state === EditState.PREVIEW}
          editedContent={editState.editedContent ?? ''}
          onApply={handleApplyPreview}
          onDiscard={handleDiscardPreview}
        />
      </ContentPaneOverlay>

      <DocumentPanel
        backHref={backHref}
        title={config.title}
        version={selectedVersion ? `V${selectedVersion.versionNumber}` : ''}
        versionDate={selectedVersion ? formatVersionDate(selectedVersion.createdAt) : ''}
        versionVariant={isViewingPrimary ? 'primary' : 'past'}
        versions={versions}
        selectedVersionId={selectedVersion?.id}
        suggestions={suggestions}
        showMarkers={showMarkers}
        isSaving={isSaving}
        hasChanges={hasChanges}
        onShowMarkersChange={setShowMarkers}
        onSave={handleSave}
        onSaveVersion={handleSaveVersion}
        onRemoveVersion={handleRemoveVersion}
        onOverwriteVersion={handleOverwriteVersion}
        onSelectVersion={handleSelectVersion}
        onMakePrimary={handleMakePrimary}
        onDownloadPdf={handleDownloadPdf}
        onFinalise={handleFinalise}
        onRemoveDocument={handleRemoveDocument}
        removeDocumentError={deleteDocumentError}
        onResetRemoveDocumentError={resetDeleteDocument}
        isRemovingDocument={isRemovingDocument}
      />

      <Box mt="md" style={{ flex: 1, overflow: 'auto' }}>
        {selectedVersion?.markdownContent && (
          <MarkdownEditor
            key={editorInstanceKey ?? selectedVersion.id}
            defaultValue={selectedVersion.markdownContent}
            bare
            onDirty={handleDirty}
            onSuggestionsChange={handleSuggestionsChange}
            onEditorReady={handleEditorReady}
            showMarkers={showMarkers}
            enableInlineEditing
            documentId={id}
            clientId={clientId}
            conversationId={conversationId}
            onInlineEditStart={handleInlineEditStart}
            onInlineEditComplete={handleInlineEditComplete}
            hasInlineEdits={diffSuggestions.length > 0}
          />
        )}
      </Box>

      {diffSuggestions.length > 0 && (
        <Box ref={actionBarRef} pos="absolute" bottom={40} left={32} right={32}>
          <SiestaActionBar
            suggestions={diffSuggestions}
            onApply={
              editState.state === EditState.DIFF_REVIEW
                ? handleApplyDiffReview
                : handleApplyInlineChanges
            }
            onDiscard={
              editState.state === EditState.DIFF_REVIEW
                ? handleDiscardDiffReview
                : handleDiscardInlineChanges
            }
          />
        </Box>
      )}
    </Flex>
  );
}
